import React, { useState, useEffect } from 'react';
import './FeedbackDetailModal.scss';

const FeedbackDetailModal = ({ feedback, onClose, onUpdate }) => {
  const [adminNotes, setAdminNotes] = useState(feedback?.admin_notes || '');
  const [selectedStatus, setSelectedStatus] = useState(feedback?.status || 'open');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setAdminNotes(feedback?.admin_notes || '');
    setSelectedStatus(feedback?.status || 'open');
    setHasChanges(false);
  }, [feedback]);

  if (!feedback) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAdminNotesChange = (e) => {
    setAdminNotes(e.target.value);
    setHasChanges(e.target.value !== (feedback.admin_notes || ''));
  };

  const handleSaveNotes = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    try {
      await onUpdate(feedback.id, { admin_notes: adminNotes });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving admin notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleStatusUpdate = async () => {
    try {
      await onUpdate(feedback.id, { status: selectedStatus });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'feedback': return 'feedback-detail-modal__type--feedback';
      case 'bug': return 'feedback-detail-modal__type--bug';
      case 'feature': return 'feedback-detail-modal__type--feature';
      case 'question': return 'feedback-detail-modal__type--question';
      case 'other': return 'feedback-detail-modal__type--other';
      default: return 'feedback-detail-modal__type--other';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return 'feedback-detail-modal__status--new';
      case 'in-progress': return 'feedback-detail-modal__status--progress';
      case 'resolved': return 'feedback-detail-modal__status--resolved';
      case 'closed': return 'feedback-detail-modal__status--closed';
      default: return 'feedback-detail-modal__status--new';
    }
  };

  return (
    <div className="feedback-detail-modal__overlay" onClick={handleOverlayClick}>
      <div className="feedback-detail-modal__container">
        {/* Header */}
        <div className="feedback-detail-modal__header">
          <div className="feedback-detail-modal__header-left">
            <span className={`feedback-detail-modal__type ${getTypeClass(feedback.type)}`}>
              {feedback.type}
            </span>
            <h2 className="feedback-detail-modal__subject">{feedback.subject}</h2>
          </div>
          <button 
            onClick={onClose}
            className="feedback-detail-modal__close-button"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="feedback-detail-modal__content">
          {/* User Info Section */}
          <div className="feedback-detail-modal__section">
            <h3 className="feedback-detail-modal__section-title">From</h3>
            <div className="feedback-detail-modal__user-info">
              <div className="feedback-detail-modal__username">
                {feedback.username || 'Anonymous'}
              </div>
              {feedback.email && (
                <div className="feedback-detail-modal__email">
                  {feedback.email}
                </div>
              )}
            </div>
          </div>

          {/* Message Section */}
          <div className="feedback-detail-modal__section">
            <h3 className="feedback-detail-modal__section-title">
              {feedback.type === 'bug' ? 'What went wrong' : 'Message'}
            </h3>
            <div className="feedback-detail-modal__message">
              {feedback.message}
            </div>
          </div>

          {/* Bug Report Details (if applicable) */}
          {feedback.type === 'bug' && (
            <>
              {feedback.page_section && (
                <div className="feedback-detail-modal__section">
                  <h3 className="feedback-detail-modal__section-title">Page/Section</h3>
                  <div className="feedback-detail-modal__text">
                    {feedback.page_section}
                  </div>
                </div>
              )}
              {feedback.reproduction_steps && (
                <div className="feedback-detail-modal__section">
                  <h3 className="feedback-detail-modal__section-title">How to Reproduce</h3>
                  <div className="feedback-detail-modal__text">
                    {feedback.reproduction_steps}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Metadata Section */}
          <div className="feedback-detail-modal__section">
            <h3 className="feedback-detail-modal__section-title">Details</h3>
            <div className="feedback-detail-modal__metadata">
              <div className="feedback-detail-modal__metadata-item">
                <span className="feedback-detail-modal__metadata-label">Status:</span>
                <span className={`feedback-detail-modal__status ${getStatusClass(feedback.status)}`}>
                  {feedback.status.replace('_', ' ')}
                </span>
              </div>
              <div className="feedback-detail-modal__metadata-item">
                <span className="feedback-detail-modal__metadata-label">Created:</span>
                <span>{formatDate(feedback.created_at)}</span>
              </div>
              <div className="feedback-detail-modal__metadata-item">
                <span className="feedback-detail-modal__metadata-label">Updated:</span>
                <span>{formatDate(feedback.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Admin Section */}
          <div className="feedback-detail-modal__section feedback-detail-modal__section--admin">
            <h3 className="feedback-detail-modal__section-title">Admin Actions</h3>
            
            {/* Status Actions */}
            <div className="feedback-detail-modal__actions">
              <div className="feedback-detail-modal__status-controls">
                <label className="feedback-detail-modal__label">Status</label>
                <select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  className="feedback-detail-modal__status-dropdown"
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {/* Admin Notes */}
            <div className="feedback-detail-modal__admin-notes">
              <label className="feedback-detail-modal__label">Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={handleAdminNotesChange}
                placeholder="Add internal notes about this feedback..."
                className="feedback-detail-modal__textarea"
                rows="4"
              />
              {hasChanges && (
                <button
                  onClick={handleSaveNotes}
                  disabled={isSaving}
                  className="feedback-detail-modal__save-btn"
                >
                  {isSaving ? 'Saving...' : 'Save Notes'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="feedback-detail-modal__footer">
          {selectedStatus !== feedback.status && (
            <button
              onClick={handleStatusUpdate}
              className="feedback-detail-modal__save-btn"
            >
              Save
            </button>
          )}
          <button
            onClick={onClose}
            className="feedback-detail-modal__close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDetailModal;